import React, { useState, useEffect } from "react";
import { ChevronLeft, CheckCircle, XCircle } from "lucide-react";
import { auth, db } from "../firebase-config";
import { doc, getDoc, updateDoc, onSnapshot } from "firebase/firestore";

export const ShowdownAnswers = ({ onBack, clientObj, userObj }) => {
  const [showdownGames, setShowdownGames] = useState([]);
  const [selectedGameIndex, setSelectedGameIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({}); // Track selected answer per question
  const [submittedAnswers, setSubmittedAnswers] = useState({}); // Track submitted answers
  const [submittingQuestion, setSubmittingQuestion] = useState(null); // Track which question is being submitted
  const [loading, setLoading] = useState(true);

  // Fetch showdown games from Firebase
  useEffect(() => {
    if (!clientObj?.clientId) {
      setLoading(false);
      return;
    }

    const fetchShowdownGames = async () => {
      try {
        // Get the client's user document directly
        const userDocRef = doc(db, "users", clientObj.clientId);
        console.log('Fetching user document for clientId:', clientObj.clientId);

        const unsubscribe = onSnapshot(userDocRef, (docSnapshot) => {
          if (docSnapshot.exists()) {
            const userData = docSnapshot.data();
            console.log("User document data keys:", Object.keys(userData));

            // Get the jukeboxShowdownGames field from the user document
            const showdownGamesData = userData.jukeboxShowdownGames;

            if (showdownGamesData && Array.isArray(showdownGamesData) && showdownGamesData.length > 0) {
              console.log("Found jukeboxShowdownGames array with", showdownGamesData.length, "games");
              console.log("First game structure:", showdownGamesData[0]);
              console.log("Game fields available:", Object.keys(showdownGamesData[0] || {}));

              const gamesArray = showdownGamesData.map((game, index) => {
                console.log(`Game ${index} has these fields:`, Object.keys(game));

                // Check for different possible field names for questions
                const questions = game.questions || game.instagramPosts || game.posts || game.items || [];
                console.log(`Game ${index} has ${questions.length} questions/posts`);

                return {
                  ...game,
                  questions: questions, // Normalize to 'questions' field
                  index: index,
                  // Check if current user has already answered this specific game
                  hasUserAnswered: game.userAnswers && game.userAnswers[auth.currentUser?.uid]
                };
              });

              setShowdownGames(gamesArray);
              console.log("Set showdownGames state with:", gamesArray);

              // Select the first active game or first game by default
              if (gamesArray.length > 0) {
                const activeGameIndex = gamesArray.findIndex(g => g.isActive);
                const indexToSelect = activeGameIndex !== -1 ? activeGameIndex : 0;
                setSelectedGameIndex(indexToSelect);

                // Load user's previous answers if they exist for this game
                const activeGame = gamesArray[indexToSelect];
                if (activeGame?.userAnswers && activeGame.userAnswers[auth.currentUser?.uid]) {
                  setSubmittedAnswers(activeGame.userAnswers[auth.currentUser?.uid]?.answers || {});
                }
              }
            } else if (showdownGamesData && typeof showdownGamesData === 'object' && !Array.isArray(showdownGamesData)) {
              // Handle single game object
              console.log("Found single game object in jukeboxShowdownGames");
              console.log("Single game structure:", showdownGamesData);
              console.log("Single game fields:", Object.keys(showdownGamesData));

              // Check for different possible field names for questions
              const questions = showdownGamesData.questions || showdownGamesData.instagramPosts ||
                               showdownGamesData.posts || showdownGamesData.items || [];
              console.log("Single game has", questions.length, "questions/posts");

              const singleGame = {
                ...showdownGamesData,
                questions: questions, // Normalize to 'questions' field
                index: 0,
                hasUserAnswered: showdownGamesData.userAnswers && showdownGamesData.userAnswers[auth.currentUser?.uid]
              };
              setShowdownGames([singleGame]);
              setSelectedGameIndex(0);

              if (singleGame?.userAnswers && singleGame.userAnswers[auth.currentUser?.uid]) {
                setSubmittedAnswers(singleGame.userAnswers[auth.currentUser?.uid]?.answers || {});
              }
            } else {
              console.log("No jukeboxShowdownGames data found or empty array");
              setShowdownGames([]);
            }
          } else {
            console.log("User document does not exist for clientId:", clientObj.clientId);
            setShowdownGames([]);
          }

          setLoading(false);
        });

        return unsubscribe;
      } catch (error) {
        console.error("Error fetching showdown games:", error);
        setLoading(false);
      }
    };

    fetchShowdownGames();
  }, [clientObj]);

  // Handle answer selection for a specific question
  const handleAnswerSelect = (questionId, answerKey) => {
    if (submittedAnswers[questionId] !== undefined) {
      // User has already submitted for this question
      return;
    }

    console.log(`Selected answer for question ${questionId}: ${answerKey}`);
    setSelectedAnswers(prev => ({
      ...prev,
      [questionId]: answerKey  // Store the key (A, B, C, D) instead of index
    }));
  };

  // Submit a single answer to Firebase
  const handleSubmitAnswer = async (questionId) => {
    const selectedGame = showdownGames[selectedGameIndex];
    const selectedAnswer = selectedAnswers[questionId];

    console.log('Submitting answer:', {
      questionId,
      selectedAnswer,
      selectedGame: selectedGame?.title,
      clientId: clientObj.clientId,
      userId: auth.currentUser?.uid
    });

    if (!selectedGame || !auth.currentUser) {
      alert('Please sign in to submit answers');
      return;
    }

    if (selectedAnswer === undefined) {
      alert('Please select an answer before submitting');
      return;
    }

    setSubmittingQuestion(questionId);

    try {
      // Get the user document reference
      const userDocRef = doc(db, "users", clientObj.clientId);
      console.log('Document path:', `users/${clientObj.clientId}`);

      // Get the current user document to update the games array
      const docSnap = await getDoc(userDocRef);
      if (docSnap.exists()) {
        const userData = docSnap.data();
        const currentGames = userData.jukeboxShowdownGames || [];

        console.log('Current games data:', currentGames);
        console.log('Selected game index:', selectedGameIndex);

        if (Array.isArray(currentGames) && currentGames.length > selectedGameIndex) {
          // Create a deep copy of the games array
          const updatedGames = [...currentGames];

          // Initialize userAnswers object if it doesn't exist
          if (!updatedGames[selectedGameIndex].userAnswers) {
            updatedGames[selectedGameIndex].userAnswers = {};
          }

          // Get existing user data or create new
          const existingUserData = updatedGames[selectedGameIndex].userAnswers[auth.currentUser.uid] || {
            uid: auth.currentUser.uid,
            displayName: userObj?.displayName || auth.currentUser.displayName || "Anonymous",
            photoURL: userObj?.photoURL || auth.currentUser.photoURL || null,
            answers: {},
            score: 0,
            submittedAt: new Date().toISOString(),  // Use ISO string instead of serverTimestamp
            totalQuestions: selectedGame.questions?.length || 0
          };

          // Update the specific answer
          existingUserData.answers[questionId] = selectedAnswer;

          // Recalculate score
          existingUserData.score = Object.keys(existingUserData.answers).reduce((score, qId) => {
            const question = selectedGame.questions.find(q => {
              const questionIdToCheck = q.id || String(selectedGame.questions.indexOf(q));
              return questionIdToCheck === qId;
            });

            console.log(`Checking answer for question ${qId}:`, {
              userAnswer: existingUserData.answers[qId],
              correctAnswer: question?.correctAnswer,
              isCorrect: existingUserData.answers[qId] === question?.correctAnswer
            });

            if (question && existingUserData.answers[qId] === question.correctAnswer) {
              return score + 1;
            }
            return score;
          }, 0);

          // Update timestamp
          existingUserData.lastAnsweredAt = new Date().toISOString();  // Use ISO string instead of serverTimestamp

          // Save back to the games array
          updatedGames[selectedGameIndex].userAnswers[auth.currentUser.uid] = existingUserData;

          // Update the user document with the modified games array
          await updateDoc(userDocRef, {
            jukeboxShowdownGames: updatedGames
          });

          // Update local state to reflect the submission
          setSubmittedAnswers(prev => ({
            ...prev,
            [questionId]: selectedAnswer
          }));
        }
      }

    } catch (error) {
      console.error("Error submitting answer - Full details:", {
        error: error,
        message: error.message,
        code: error.code,
        questionId,
        selectedAnswer,
        clientId: clientObj.clientId,
        userId: auth.currentUser?.uid
      });

      // Show more specific error message
      let errorMessage = 'Failed to submit answer. ';
      if (error.code === 'permission-denied') {
        errorMessage += 'You do not have permission to update this game.';
      } else if (error.code === 'not-found') {
        errorMessage += 'The game data could not be found.';
      } else {
        errorMessage += error.message || 'Please try again.';
      }

      alert(errorMessage);
    } finally {
      setSubmittingQuestion(null);
    }
  };

  const selectedGame = showdownGames[selectedGameIndex];

  // Debug logging
  console.log("Current state - showdownGames:", showdownGames);
  console.log("Current state - selectedGameIndex:", selectedGameIndex);
  console.log("Current state - selectedGame:", selectedGame);
  if (selectedGame) {
    console.log("Selected game has questions field:", !!selectedGame.questions);
    console.log("Number of questions:", selectedGame.questions?.length || 0);
    if (selectedGame.questions && selectedGame.questions.length > 0) {
      console.log("First question structure:", selectedGame.questions[0]);
    }
  }

  if (loading) {
    return (
      <div className="showdown-answers">
        <div className="answers-container">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading showdown games...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!selectedGame || showdownGames.length === 0) {
    return (
      <div className="showdown-answers">
        <div className="answers-container">
          <div className="answers-header">
            <button onClick={onBack} className="btn-back">
              <ChevronLeft size={20} />
              Back
            </button>
            <h2 className="text-2xl font-bold text-white">
              Jukebox Showdown
            </h2>
            <div className="header-spacer"></div>
          </div>
          <div className="no-games-message">
            <p>No showdown games available at the moment.</p>
            <p>Check back later for new challenges!</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="showdown-answers">
      <div className="answers-container">
        {/* Header */}
        <div className="answers-header">
          <button onClick={onBack} className="btn-back">
            <ChevronLeft size={20} />
            Back
          </button>
          <h2 className="text-2xl font-bold text-white">
            Jukebox Showdown
          </h2>
          <div className="header-spacer"></div>
        </div>

        {/* Game Selector */}
        {showdownGames.length > 1 && (
          <div className="game-selector">
            {showdownGames.map((game, index) => (
              <button
                key={index}
                className={`game-btn ${selectedGameIndex === index ? 'active' : ''} ${game.hasUserAnswered ? 'answered' : ''}`}
                onClick={() => {
                  setSelectedGameIndex(index);
                  setSelectedAnswers({});
                  const currentUserId = auth.currentUser?.uid;
                  setSubmittedAnswers((currentUserId && game.userAnswers?.[currentUserId]?.answers) || {});
                }}
              >
                {game.title || `Game ${index + 1}`}
                {game.hasUserAnswered && <CheckCircle size={16} />}
              </button>
            ))}
          </div>
        )}

        {/* Game Info */}
        <div className="game-info-card" style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#1f2937', borderRadius: '0.5rem' }}>
          <h3 className="game-title" style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#ffffff' }}>
            {selectedGame.title || "Music Challenge"}
          </h3>
          <p className="game-description" style={{ color: '#e5e7eb' }}>
            {selectedGame.description || "Test your music knowledge with these Instagram posts!"}
          </p>
        </div>


        {/* Questions with Instagram Posts */}
        <div className="questions-list">
          {selectedGame.questions?.map((question, index) => {
            const questionId = question.id || String(index);
            const isAnswered = submittedAnswers[questionId] !== undefined;
            const selectedAnswer = isAnswered ? submittedAnswers[questionId] : selectedAnswers[questionId];
            const showCorrect = isAnswered;

            // Debug log for each question
            console.log(`Question ${index} structure:`, question);
            console.log(`Question ${index} has options/answers:`, question.options || question.answers || question.choices);

            return (
              <div key={question.id || index} className="question-card">
                <div className="question-header" style={{ marginBottom: '1rem' }}>
                  <span className="question-number" style={{ color: '#ffffff', fontWeight: 'bold', fontSize: '1.2rem' }}>Question {index + 1}</span>
                  {isAnswered && (
                    submittedAnswers[question.id] === question.correctAnswer ? (
                      <CheckCircle size={20} className="correct-icon" />
                    ) : (
                      <XCircle size={20} className="incorrect-icon" />
                    )
                  )}
                </div>

                {/* Instagram Post Display */}
                {(() => {
                  // Check for Instagram URL in various fields
                  const instagramUrl = question.instagramPost?.url || question.instagramPost ||
                                       question.url || question.instagramUrl || question.post;

                  if (!instagramUrl) {
                    return null;
                  }

                  // Check if it's an Instagram URL
                  if (typeof instagramUrl === 'string' && instagramUrl.includes('instagram.com')) {
                    // Extract the Instagram post ID from the URL
                    const postIdMatch = instagramUrl.match(/\/p\/([A-Za-z0-9_-]+)/);
                    const reelIdMatch = instagramUrl.match(/\/reel\/([A-Za-z0-9_-]+)/);
                    const postId = postIdMatch?.[1] || reelIdMatch?.[1];

                    if (postId) {
                      // Use Instagram oEmbed for proper embedding
                      return (
                        <div className="instagram-post" style={{
                          marginBottom: '1rem',
                          display: 'flex',
                          justifyContent: 'center'
                        }}>
                          <iframe
                            src={`https://www.instagram.com/p/${postId}/embed`}
                            title={`Instagram post ${index + 1}`}
                            width="400"
                            height="480"
                            style={{
                              border: 'none',
                              borderRadius: '0.5rem',
                              backgroundColor: 'white',
                              maxWidth: '100%'
                            }}
                          ></iframe>
                        </div>
                      );
                    }
                  }

                  // Fallback for direct media URLs
                  if (typeof instagramUrl === 'string') {
                    if (instagramUrl.match(/\.(jpg|jpeg|png|gif)$/i)) {
                      return (
                        <div className="instagram-post" style={{ marginBottom: '1rem' }}>
                          <img
                            src={instagramUrl}
                            alt={`Instagram post ${index + 1}`}
                            style={{
                              width: '100%',
                              maxHeight: '400px',
                              objectFit: 'contain',
                              borderRadius: '0.5rem'
                            }}
                          />
                        </div>
                      );
                    } else if (instagramUrl.match(/\.(mp4|webm|ogg)$/i)) {
                      return (
                        <div className="instagram-post" style={{ marginBottom: '1rem' }}>
                          <video
                            src={instagramUrl}
                            controls
                            style={{
                              width: '100%',
                              maxHeight: '400px',
                              borderRadius: '0.5rem'
                            }}
                          />
                        </div>
                      );
                    }
                  }

                  // If we have an object with URL property
                  if (typeof instagramUrl === 'object' && instagramUrl.url) {
                    return (
                      <div className="instagram-post" style={{ marginBottom: '1rem' }}>
                        <a href={instagramUrl.url} target="_blank" rel="noopener noreferrer"
                           style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                          View Instagram Post
                        </a>
                      </div>
                    );
                  }

                  return null;
                })()}

                <p className="question-text" style={{ color: '#f3f4f6', fontSize: '1.1rem', marginBottom: '1rem', marginTop: '0.5rem' }}>
                  {question.question || question.text || question.prompt || "Select the correct answer:"}
                </p>

                <div className="options-list">
                  {(() => {
                    // Get answers - could be array or object
                    const answersData = question.options || question.answers || question.choices;

                    let answersArray = [];

                    // Check if answers is an object with A, B, C, D keys
                    if (answersData && typeof answersData === 'object' && !Array.isArray(answersData)) {
                      // Convert object {A: 'answer1', B: 'answer2', ...} to array
                      const answerKeys = ['A', 'B', 'C', 'D'];
                      answersArray = answerKeys
                        .filter(key => answersData[key] !== undefined)
                        .map(key => ({
                          key: key,
                          text: answersData[key]
                        }));
                    } else if (Array.isArray(answersData)) {
                      // If it's already an array, use it
                      answersArray = answersData.map((text, index) => ({
                        key: String.fromCharCode(65 + index), // A, B, C, D
                        text: text
                      }));
                    }

                    if (answersArray.length === 0) {
                      return <p>No answer options available</p>;
                    }

                    return answersArray.map((option, optionIndex) => {
                      const isSelected = selectedAnswer === option.key;
                      const isCorrect = question.correctAnswer === option.key;
                      const isWrong = isAnswered && selectedAnswer === option.key && !isCorrect;

                      return (
                    <button
                      key={optionIndex}
                      onClick={() => !isAnswered && handleAnswerSelect(questionId, option.key)}
                      disabled={isAnswered}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        marginBottom: '0.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        backgroundColor: isSelected ? '#10b981' :
                                        isAnswered && isCorrect ? '#10b981' :
                                        isWrong ? '#ef4444' :
                                        '#374151',
                        color: '#ffffff',
                        border: isSelected ? '2px solid #34d399' : '2px solid transparent',
                        borderRadius: '0.5rem',
                        cursor: isAnswered ? 'not-allowed' : 'pointer',
                        opacity: isAnswered && !isSelected && !isCorrect ? 0.5 : 1,
                        transition: 'all 0.2s'
                      }}
                    >
                      <span style={{
                        backgroundColor: isSelected ? '#065f46' : '#1f2937',
                        color: '#ffffff',
                        width: '2rem',
                        height: '2rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '0.25rem',
                        fontWeight: 'bold',
                        fontSize: '1.1rem'
                      }}>
                        {option.key}
                      </span>
                      <span style={{
                        flex: 1,
                        textAlign: 'left',
                        fontSize: '1rem',
                        color: '#ffffff'
                      }}>{option.text || option}</span>
                      {showCorrect && isCorrect && (
                        <CheckCircle size={16} className="option-icon correct" />
                      )}
                      {showCorrect && isWrong && (
                        <XCircle size={16} className="option-icon incorrect" />
                      )}
                    </button>
                      );
                    });
                  })()}
                </div>

                {/* Submit button for this question */}
                {!isAnswered && (
                  <button
                    className="btn-submit-question"
                    onClick={() => handleSubmitAnswer(questionId)}
                    disabled={selectedAnswer === undefined || submittingQuestion === questionId}
                    style={{
                      width: '100%',
                      marginTop: '1rem',
                      padding: '1rem',
                      backgroundColor: selectedAnswer !== undefined ? '#10b981' : '#4b5563',
                      color: '#ffffff',
                      borderRadius: '0.5rem',
                      cursor: selectedAnswer !== undefined ? 'pointer' : 'not-allowed',
                      opacity: selectedAnswer !== undefined ? 1 : 0.6,
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      transition: 'all 0.2s',
                      border: 'none'
                    }}
                  >
                    {submittingQuestion === questionId ? 'Submitting...' : 'Submit Answer'}
                  </button>
                )}

                {isAnswered && question.explanation && (
                  <div className="explanation">
                    <p className="explanation-label">Explanation:</p>
                    <p className="explanation-text">{question.explanation}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>


        {/* Simple Score Display */}
        {selectedGame && Object.keys(submittedAnswers).length > 0 && (
          <div style={{
            marginTop: '2rem',
            padding: '1rem',
            backgroundColor: '#1f2937',
            borderRadius: '0.5rem',
            textAlign: 'center',
            border: '1px solid #374151'
          }}>
            <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#ffffff' }}>
              Questions Answered: <span style={{ color: '#10b981' }}>{Object.keys(submittedAnswers).length}</span> / <span style={{ color: '#e5e7eb' }}>{selectedGame.questions?.length || 0}</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};